import React, { useState, useEffect, useRef, useMemo } from 'react';
import { fetchAllLists } from '../hooks/useData';
import { formatRelative, formatAbsolute } from '../utils/time';

const STORAGE_KEY = 'lists_last_search';

export default function SearchModal({ userId, boards, currentBoardId, onJump, onClose }) {
  const [q, setQ] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  // Walter 2026-05-16: debounced query for the scan. Typing into the
  // input updates `q` immediately (for the controlled <input>) but the
  // scan only re-runs ~180ms after the user stops typing. Without
  // this, every keystroke ran an O(allLists × content-length)
  // synchronous indexOf-loop on the main thread — fine for small
  // accounts, but noticeable as a typing freeze once allLists carries
  // a few dozen lists with thousands of chars each.
  const [debouncedQ, setDebouncedQ] = useState(q);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 180);
    return () => clearTimeout(t);
  }, [q]);
  const [allLists, setAllLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    // Select all text so user can immediately type a new query
    inputRef.current?.select();
  }, []);

  // One-shot fetch when the modal opens. Cheaper than a live listener across all boards —
  // snapshot is ~1 read per list in exchange for no echo-reads while editing.
  //
  // Walter 2026-05-19: hardened against both REJECTION (.catch) and a
  // HANGING promise (timeout race). Previously any path that didn't
  // resolve left setLoading(true) stuck and the modal stayed in
  // "loading…" forever; user had to hard-refresh to dismiss. Now a
  // hung fetch resolves to [] after 12 seconds so the modal becomes
  // dismissable + shows "(no results)" instead of an eternal spinner.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const timeoutMs = 12000;
    let timer = null;
    const timeoutPromise = new Promise(resolve => {
      timer = setTimeout(() => {
        console.warn(`[SearchModal] fetch timed out after ${timeoutMs}ms`);
        resolve([]);
      }, timeoutMs);
    });
    Promise.race([
      fetchAllLists(userId, boards),
      timeoutPromise,
    ])
      .then(lists => {
        if (timer) clearTimeout(timer);
        if (!cancelled) {
          setAllLists(lists || []);
          setLoading(false);
        }
      })
      .catch(err => {
        if (timer) clearTimeout(timer);
        console.warn('[SearchModal] fetch failed:', err);
        if (!cancelled) {
          setAllLists([]);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [userId, boards]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleChange = (val) => {
    setQ(val);
    localStorage.setItem(STORAGE_KEY, val);
  };

  // Build results: each hit is a {list, excerpt, matchIndex} object.
  // Walter 2026-05-16: useMemo (not useCallback+IIFE — the earlier
  // shape recomputed on every render where deps were stable but the
  // memoization wasn't actually saving anything). Now deps are the
  // debounced query + the all-lists fetch result, so the scan only
  // runs when one of those genuinely changes. Caps result count at
  // 200 so even a very common single-letter query doesn't blow up
  // the render.
  const results = useMemo(() => {
    if (!debouncedQ.trim()) return [];
    const ql = debouncedQ.toLowerCase();
    const hits = [];
    const LIMIT = 200;

    for (const l of allLists) {
      if (hits.length >= LIMIT) break;
      const titleMatch = l.title?.toLowerCase().includes(ql);
      const content = l.content || '';
      const contentMatches = getAllMatches(content, ql);

      if (titleMatch && contentMatches.length === 0) {
        hits.push({
          key: `${l.id}-title`,
          list: l,
          excerpt: content.slice(0, 80) || '(empty)',
          matchInTitle: true,
          charOffset: null,
        });
      } else {
        for (let i = 0; i < contentMatches.length; i++) {
          if (hits.length >= LIMIT) break;
          const matchIdx = contentMatches[i];
          hits.push({
            key: `${l.id}-${matchIdx}`,
            list: l,
            excerpt: getExcerpt(content, matchIdx, debouncedQ.length),
            matchInTitle: titleMatch && i === 0,
            charOffset: matchIdx,
          });
        }
      }
    }
    return hits;
  }, [debouncedQ, allLists]);

  const isSearching = q.trim() !== debouncedQ.trim();

  function getAllMatches(text, query) {
    const indices = [];
    const tl = text.toLowerCase();
    let idx = tl.indexOf(query);
    while (idx !== -1) {
      indices.push(idx);
      idx = tl.indexOf(query, idx + 1);
    }
    return indices;
  }

  function getExcerpt(text, matchIdx, queryLen) {
    const CONTEXT = 40;
    const start = Math.max(0, matchIdx - CONTEXT);
    const end = Math.min(text.length, matchIdx + queryLen + CONTEXT);
    return (start > 0 ? '…' : '') + text.slice(start, end) + (end < text.length ? '…' : '');
  }

  function highlight(text, query) {
    if (!query.trim()) return text;
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase()
        ? <mark key={i} style={{
            background: 'var(--accent)', color: '#000',
            borderRadius: '2px', padding: '0 1px'
          }}>{part}</mark>
        : part
    );
  }

  const isOnCurrentBoard = (list) => list.boardId === currentBoardId;

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'flex-start',
        justifyContent: 'center', paddingTop: '100px',
      }}
    >
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border-bright)',
        borderRadius: '6px', width: '600px',
        maxWidth: '90vw',
        boxShadow: 'var(--shadow-modal-deep)',
        overflow: 'hidden',
      }}>
        {/* Input */}
        <div style={{
          display: 'flex', alignItems: 'center',
          borderBottom: '1px solid var(--border)',
          padding: '0 16px',
        }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, color: 'var(--text-dimmer)' }}>
            <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.4"/>
            <path d="M9.5 9.5l3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
          <input
            ref={inputRef}
            value={q}
            onChange={e => handleChange(e.target.value)}
            placeholder="search across all boards…"
            style={{
              flex: 1, background: 'none', border: 'none', outline: 'none',
              color: 'var(--text)', fontFamily: 'var(--mono)',
              fontSize: '14px', padding: '14px 12px',
            }}
          />
          {q && (
            <button
              onClick={() => handleChange('')}
              title="clear"
              style={{
                background: 'none', border: 'none', color: 'var(--text-dimmer)',
                cursor: 'pointer', fontSize: '16px', padding: '0 4px',
              }}
            >×</button>
          )}
          {/* Walter 2026-06-16: explicit close button — the modal previously
              only closed on Esc / backdrop click. Matches the ✕-to-close
              affordance used elsewhere. */}
          <button
            onClick={onClose}
            title="close (Esc)"
            aria-label="close search"
            style={{
              background: 'none', border: 'none', color: 'var(--text-dim)',
              cursor: 'pointer', fontSize: '18px', lineHeight: 1,
              padding: '0 2px 0 8px', marginLeft: 4,
            }}
          >✕</button>
        </div>

        {/* Results */}
        <div style={{ maxHeight: '460px', overflowY: 'auto' }}>
          {loading && (
            <div style={{
              padding: '24px', textAlign: 'center',
              color: 'var(--text-dimmer)', fontFamily: 'var(--mono)', fontSize: '12px',
            }}>
              loading…
            </div>
          )}

          {!loading && q.trim() && results.length === 0 && (
            <div style={{
              padding: '24px', textAlign: 'center',
              color: 'var(--text-dimmer)', fontFamily: 'var(--mono)', fontSize: '12px'
            }}>
              no results for "{q}"
            </div>
          )}

          {results.map((hit, i) => {
            const isFirst = i === 0 || results[i - 1].list.id !== hit.list.id;
            const onCurrentBoard = isOnCurrentBoard(hit.list);

            return (
              <div
                key={hit.key}
                onClick={() => { onJump(hit.list, hit.charOffset ?? 0); onClose(); }}
                style={{
                  padding: isFirst ? '10px 16px 6px' : '4px 16px 6px',
                  borderBottom: '1px solid var(--border)',
                  cursor: 'pointer', transition: 'background 0.1s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {/* Panel + board label — only on first hit for this list */}
                {isFirst && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    marginBottom: '3px',
                  }}>
                    <span style={{
                      fontFamily: 'var(--mono)', fontSize: '11px',
                      fontWeight: 600,
                      color: hit.matchInTitle ? 'var(--accent)' : 'var(--text)',
                    }}>
                      {highlight(hit.list.title || 'untitled', q)}
                    </span>
                    <span style={{
                      fontFamily: 'var(--mono)', fontSize: '10px',
                      color: 'var(--text-dimmer)',
                    }}>
                      on:
                    </span>
                    <span style={{
                      fontFamily: 'var(--mono)', fontSize: '10px',
                      color: onCurrentBoard ? 'var(--accent-dim)' : 'var(--text-dimmer)',
                    }}>
                      {hit.list.boardName}
                      {!onCurrentBoard && (
                        <span style={{ marginLeft: '4px', fontSize: '9px', opacity: 0.6 }}>
                          (switch board)
                        </span>
                      )}
                    </span>
                    {/* Relative timestamp on the right, tabular-nums for a
                        stable column of times across rows. */}
                    <span style={{ flex: 1 }} />
                    {hit.list.updatedAt && (
                      <span
                        title={formatAbsolute(hit.list.updatedAt)}
                        style={{
                          fontFamily: 'var(--mono)', fontSize: '10px',
                          color: 'var(--text-dimmer)',
                          fontVariantNumeric: 'tabular-nums',
                          flexShrink: 0,
                        }}
                      >
                        {formatRelative(hit.list.updatedAt)}
                      </span>
                    )}
                  </div>
                )}

                {/* Excerpt */}
                <div style={{
                  fontSize: '11px', color: 'var(--text-dim)',
                  lineHeight: 1.6, fontFamily: 'var(--mono)',
                  paddingLeft: isFirst ? '0' : '12px',
                }}>
                  {highlight(hit.excerpt, q)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer hint */}
        <div style={{
          padding: '8px 16px',
          borderTop: '1px solid var(--border)',
          color: 'var(--text-dimmer)', fontFamily: 'var(--mono)', fontSize: '10px',
          display: 'flex', justifyContent: 'space-between',
        }}>
          <span>
            {loading
              ? 'loading lists…'
              : q.trim()
                ? (() => {
                    const uniqueLists = new Set(results.map(r => r.list.id)).size;
                    if (results.length === 0) return 'no hits';
                    return `${results.length} hit${results.length !== 1 ? 's' : ''}` +
                      (uniqueLists !== results.length
                        ? ` across ${uniqueLists} list${uniqueLists !== 1 ? 's' : ''}`
                        : '');
                  })()
                : 'type to search across all boards'}
          </span>
          <span>esc to close</span>
        </div>
      </div>
    </div>
  );
}
